import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';

import { NgxOtpInputComponentOptions } from 'ngx-otp-input';

import { ApiService } from '../../services/api.service';
import { NotificationService } from '../../services/notification.service';
import { REQUEST_OTP, VERIFY_OTP } from '../../../config';
import { TwoFactorAuthService, TwoFactorAuthType } from '../../../services/two-factor-auth.service';
import { of, Subscription, timer } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { Globals } from '../../services/globals';

interface TwoFactorVerifyResponse {
  status: string;
  message?: string;
  data?: unknown;
  error_info?: { message?: string };
}

interface RequestOtpResponse {
  status: string;
  message?: string;
  error_info?: { message?: string };
}

@Component({
  selector: 'app-two-factor-auth-dialog',
  templateUrl: './two-factor-auth-dialog.component.html',
  styleUrls: ['./two-factor-auth-dialog.component.scss'],
})
export class TwoFactorAuthDialogComponent implements OnInit, OnDestroy {
  readonly authType: TwoFactorAuthType;
  readonly effectiveAuthType: TwoFactorAuthType;
  readonly otpLength: number;
  readonly email: string | null;

  assetsUrl: string;

  otpOptions: NgxOtpInputComponentOptions;
  otpValue = '';

  isVerifying = false;

  timeRemaining = '00:00';
  private readonly otpValiditySeconds = 10 * 60;
  private otpExpiresAtMs: number | null = null;
  private countdownSub?: Subscription;

  constructor(
    private readonly dialogRef: MatDialogRef<TwoFactorAuthDialogComponent>,
    private readonly apiService: ApiService,
    private readonly notificationService: NotificationService,
    private readonly twoFactorAuthService: TwoFactorAuthService,
    private readonly globals: Globals
  ) {
    this.authType = this.twoFactorAuthService.snapshot.authType;
    // Backend can return 'disabled' to mean "2FA selectively enforced"; in that mode we still use email OTP.
    this.effectiveAuthType = this.authType === 'disabled' ? 'otp' : this.authType;
    this.email = this.twoFactorAuthService.snapshot.email ?? null;
    this.otpLength = this.effectiveAuthType === 'ga' ? 6 : 4;
    this.assetsUrl = this.globals.getAssetsUrl();
  }

  ngOnInit(): void {
    this.otpOptions = {
      otpLength: this.otpLength,
      autoFocus: true,
      showBlinkingCursor: true,
      inputMode: 'numeric',
      // allow only digits (use + so full-code paste like "123456" is accepted by ngxOtpPaste)
      regexp: /^[0-9]+$/,
    };

    if (this.effectiveAuthType === 'otp') {
      this.requestOtp();
    }
  }

  ngOnDestroy(): void {
    this.countdownSub?.unsubscribe();
  }

  get subtitle(): string {
    return this.effectiveAuthType === 'ga'
      ? 'To proceed please enter the verification code from your authenticator app.'
      : `To proceed please enter the verification code sent to your registered email:`;
  }

  onOtpComplete(otp: string): void {
    this.otpValue = otp;
    this.verify();
  }

  onOtpChange(parts: string[]): void {
    this.otpValue = parts.join('');
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  requestOtp(): void {
    this.apiService.get(REQUEST_OTP, { params: { type: 'auth' } }).subscribe({
      next: (res: RequestOtpResponse) => {
        if (res?.status === 'success') {
          this.notificationService.showSuccess(res?.message ?? 'OTP sent.');
          this.startOtpTimer();
        } else {
          this.notificationService.showError(res?.error_info?.message ?? res?.message ?? 'Unable to send OTP.');
          this.expireOtpTimer();
        }
      },
      error: () => {
        this.notificationService.showError('Unable to send OTP.');
        this.expireOtpTimer();
      },
    });
  }

  private expireOtpTimer(): void {
    // Allow immediate resend on failure by forcing timer to expire and stopping any running countdown.
    this.otpExpiresAtMs = null;
    this.countdownSub?.unsubscribe();
    this.timeRemaining = '00:00';
  }

  private startOtpTimer(): void {
    this.otpExpiresAtMs = Date.now() + this.otpValiditySeconds * 1000;
    this.updateTimeRemaining();

    this.countdownSub?.unsubscribe();
    this.countdownSub = timer(1000, 1000).subscribe(() => {
      this.updateTimeRemaining();
      if (this.timeRemaining === '00:00') {
        this.countdownSub?.unsubscribe();
      }
    });
  }

  private updateTimeRemaining(): void {
    if (!this.otpExpiresAtMs) {
      this.timeRemaining = this.formatSeconds(this.otpValiditySeconds);
      return;
    }
    const remainingSeconds = Math.max(0, Math.ceil((this.otpExpiresAtMs - Date.now()) / 1000));
    this.timeRemaining = this.formatSeconds(remainingSeconds);
  }

  private formatSeconds(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  verify(): void {
    if (this.isVerifying || this.otpValue.length !== this.otpLength) {
      return;
    }

    this.isVerifying = true;

    const payload = {
      otp: this.otpValue,
      authType: this.effectiveAuthType,
    };

    this.apiService
      .post(VERIFY_OTP, payload)
      .pipe(
        switchMap((res: TwoFactorVerifyResponse) => {
          if (res?.status !== 'success') {
            this.notificationService.showError(res?.error_info?.message ?? res?.message ?? 'Invalid code.');
            return of(false);
          }

          // verify_otp does not return validTill; fetch it from 2FA details/status API.
          return this.twoFactorAuthService.reloadFromServer();
        }),
        finalize(() => {
          this.isVerifying = false;
        })
      )
      .subscribe({
        next: (isLoaded: boolean) => {
          if (!isLoaded) {
            return;
          }

          const validTill = this.twoFactorAuthService.snapshot.validTill;
          if (!validTill) {
            this.notificationService.showError('Verification succeeded but validTill was not returned by 2FA details.');
            return;
          }

          this.dialogRef.close(true);
        },
        error: () => {
          this.notificationService.showError('Unable to verify code.');
        },
      });
  }
}


